import axiosInstance from '@/api/axiosInstance';
import { BaseResponse } from '@/types/common';
import {
  VipSuppIdSearchParams,
  VipSuppIdListResponse,
  VipSuppIdRecord,
  VipSuppIdFormData,
  VipSuppIdStats,
  VipSuppIdStatus,
  VipBeautifulNumberResp,
} from './types';

const baseUrl = `${SUPP_ADMIN_BASE_URL}/supp-management`;

/**
 * 获取 VIP SUPPID 列表
 */
export const getVipSuppIdList = async (
  params: VipSuppIdSearchParams
): Promise<VipSuppIdListResponse> => {
  const response = await axiosInstance.get<BaseResponse<{
    records: VipBeautifulNumberResp[];
    total: number;
    currentPage: number;
    pageSize: number;
  }>>(
    `${baseUrl}/vip/number/pageList`,
    {
      params: {
        currentPage: params.currentPage || 1,
        pageSize: params.pageSize || 10,
      },
    }
  );

  // 检查响应状态
  if (response.data.status === 200 && response.data.data) {
    // 转换 API 返回的数据格式
    const apiData = response.data.data;
    const records: VipSuppIdRecord[] = apiData.records.map((item) => ({
      id: item.id,
      beautifulNumber: item.beautifulNumber,
      status: item.status as VipSuppIdStatus,
      deleteStatus: item.deleteStatus,
      updateTime: item.updateTime,
      updateUserId: item.updateUserId,
      updateUserName: item.updateUserName,
    }));

    return {
      records,
      total: apiData.total,
      currentPage: apiData.currentPage,
      pageSize: apiData.pageSize,
    };
  }

  throw new Error(response.data.message || '获取 VIP SUPPID 列表失败');
};

/**
 * 保存 VIP SUPPID（新增/编辑统一接口）
 */
export const saveVipSuppId = async (
  data: VipSuppIdFormData
): Promise<{ success: boolean; message: string }> => {
  // 构建请求参数：编辑时传 id，新增时不传 id
  // beautifulNumber 需要转换为数字类型
  const beautifulNumber = Number(data.beautifulNumber);
  if (isNaN(beautifulNumber)) {
    throw new Error('VIP SUPPID 必须是有效的数字');
  }

  const requestData: { beautifulNumber: number; id?: number } = {
    beautifulNumber,
  };
  
  // 如果是编辑（有 id），则添加 id 字段
  if (data.id) {
    requestData.id = data.id;
  }

  const response = await axiosInstance.post<BaseResponse<boolean>>(
    `${baseUrl}/vip/number/save`,
    requestData
  );

  if (response.data.status === 200) {
    return { success: true, message: data.id ? '更新成功' : '创建成功' };
  }

  throw new Error(response.data.message || (data.id ? '更新失败' : '创建失败'));
};

/**
 * 删除 VIP SUPPID
 */
export const deleteVipSuppId = async (
  id: number,
  beautifulNumber: number
): Promise<{ success: boolean; message: string }> => {
  const response = await axiosInstance.post<BaseResponse<boolean>>(
    `${baseUrl}/vip/number/delete`,
    {
      id,
      beautifulNumber,
    }
  );

  if (response.data.status === 200) {
    return { success: true, message: '删除成功' };
  }

  throw new Error(response.data.message || '删除失败');
};

/**
 * 获取 VIP SUPPID 统计信息
 */
export const getVipSuppIdStats = async (): Promise<VipSuppIdStats> => {
  const response = await axiosInstance.get<BaseResponse<VipSuppIdStats>>(
    `${baseUrl}/vip/number/usedStatistics`
  );

  if (response.data.status === 200 && response.data.data) {
    return response.data.data;
  }

  throw new Error(response.data.message || '获取统计信息失败');
};

